// src/pages/Terms.js
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AnimatedSocialLinks from '../components/AnimatedSocialLinks';
import './Home.css';

export default function Terms() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-x-hidden">
      {/* ───── ANIMATED BACKGROUND BLOBS ───── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* ───── SEO ───── */}
      <Helmet>
        <title>Terms of Service - SErraND Kenya</title>
        <meta
          name="description"
          content="Review SErraND's Terms of Service, outlining the rules and guidelines for using our platform to connect with service providers in Kenya."
        />
        <meta
          name="keywords"
          content="SErraND, terms of service, Kenya, service providers, platform guidelines, user agreement"
        />
        <meta name="author" content="Marvin Useki" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.serrand.org/terms/" />
        <meta property="og:title" content="Terms of Service - SErraND Kenya" />
        <meta
          property="og:description"
          content="Understand the Terms of Service for using SErraND, your trusted platform for service providers in Kenya."
        />
        <meta property="og:url" content="https://www.serrand.org/terms/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.serrand.org/images/terms-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Terms of Service - SErraND Kenya" />
        <meta name="twitter:description" content="SErraND's Terms of Service for users in Kenya." />
        <meta name="twitter:image" content="https://www.serrand.org/images/terms-image.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'SErraND Terms of Service',
            url: 'https://www.serrand.org/terms/',
            description:
              'Read the Terms of Service for SErraND, governing the use of our platform connecting clients with trusted service providers in Kenya.',
            publisher: {
              '@type': 'Organization',
              name: 'SErraND',
              url: 'https://www.serrand.org',
              logo: 'https://www.serrand.org/images/logo.png',
            },
          })}
        </script>
      </Helmet>

      {/* ───── NAVIGATION (Home.js style, no auth) ───── */}
      <nav className="relative z-40 bg-gray-900 bg-opacity-80 backdrop-blur-lg border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2 group">
              <div className="logo-container bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent group-hover:from-blue-400 group-hover:to-purple-500 transition-all">
                <span className="text-2xl font-bold">SE</span>
                <span className="text-2xl font-bold">RRA</span>
                <span className="text-2xl font-bold">ND</span>
              </div>
            </Link>

            <div className="hidden md:flex md:items-center md:space-x-6">
              <Link to="/" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800">Home</Link>
              <Link to="/profile" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800">Profile</Link>
              <Link to="/contact" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800">Contact</Link>
              <Link to="/search" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800">Search Providers</Link>
              <Link to="/terms" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800">Terms</Link>
              <Link to="/privacy" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800">Privacy</Link>
            </div>

            <button
              className="md:hidden relative w-8 h-8 flex flex-col justify-center items-center"
              onClick={() => document.getElementById('mobile-menu')?.classList.toggle('hidden')}
              aria-label="Toggle menu"
            >
              <span className="w-6 h-0.5 bg-white transition-all duration-300"></span>
              <span className="w-6 h-0.5 bg-white transition-all duration-300 mt-1"></span>
              <span className="w-6 h-0.5 bg-white transition-all duration-300 mt-1"></span>
            </button>
          </div>
        </div>

        <div id="mobile-menu" className="md:hidden hidden bg-gray-900 bg-opacity-95 backdrop-blur-lg border-t border-gray-700">
          <div className="px-4 py-4 space-y-2">
            <Link to="/" className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800">Home</Link>
            <Link to="/profile" className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800">Profile</Link>
            <Link to="/contact" className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800">Contact</Link>
            <Link to="/search" className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800">Search Providers</Link>
            <Link to="/terms" className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800">Terms</Link>
            <Link to="/privacy" className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800">Privacy</Link>
          </div>
        </div>
      </nav>

      {/* ───── TERMS CONTENT - 100% YOURS, UNCHANGED ───── */}
      <section className="relative py-20 px-4 max-w-6xl mx-auto z-10">
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-4">SErraND Terms of Service</h1>
          <p className="text-xl text-gray-300">Last Updated: May 31, 2025</p>
        </div>

        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 p-8 md:p-12 shadow-2xl backdrop-blur-lg max-w-5xl mx-auto space-y-8 text-gray-300 leading-relaxed">
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Introduction</h2>
            <p>
              Welcome to SErraND, a vibrant platform designed to connect clients with trusted service providers across Kenya. By accessing or using SErraND.org, whether as a client seeking services or a service provider offering your expertise, you agree to be bound by these Terms of Service (“ToS”). These terms govern your use of our platform, including all content, functionality, and services offered. If you do not agree with these terms, please do not access or use our platform.
            </p>
            <p className="mt-4">
              SErraND is committed to fostering a safe, transparent, and reliable community. These terms outline your rights and responsibilities to ensure a positive experience for all users.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. User-Generated Content and Acceptable Use</h2>
            <p>
              SErraND allows users to create and share content, such as service listings, profiles, and reviews, to promote their services or engage with the community. All content must adhere to the following guidelines:
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2 ml-4">
              <li>Be lawful, respectful, and appropriate.</li>
              <li>Not contain illegal, defamatory, harassing, hateful, or obscene material.</li>
              <li>Not infringe on intellectual property rights, including copyrights or trademarks.</li>
              <li>Not misrepresent your identity, qualifications, or services.</li>
            </ul>
            <p className="mt-4">
              The use of SErraND’s name, logo, or branding in a misleading or unauthorized manner is strictly prohibited. We reserve the right to remove any content that violates these guidelines and may suspend or terminate accounts that repeatedly violate our policies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. Pricing and Promotions</h2>
            <p>
              SErraND is free to use for both clients and service providers. However, we charge a 10% commission on all transactions facilitated through the platform. This commission supports the maintenance and improvement of our services and may be adjusted at SErraND’s discretion with prior notice.
            </p>
            <p className="mt-4">
              Service providers may opt for promoted placements to enhance their visibility on the platform. These promotions are optional, and participation does not influence SErraND’s impartiality or guarantee service quality. Details about promotion costs and benefits are available in your provider dashboard.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. Prohibited Conduct</h2>
            <p>
              To maintain a safe and respectful community, users are prohibited from:
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2 ml-4">
              <li>Engaging in spamming, phishing, or unsolicited communications.</li>
              <li>Harassing, threatening, or discriminating against other users.</li>
              <li>Conducting illegal activities, including fraud or misrepresentation.</li>
              <li>Attempting to hack, disrupt, or bypass SErraND’s security measures.</li>
            </ul>
            <p className="mt-4">
              SErraND will cooperate fully with Kenyan law enforcement authorities in response to illegal activities. Violations may result in account suspension, termination, or legal action.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. Intellectual Property</h2>
            <p>
              SErraND owns all rights to its branding, including the “SErraND” name, logo, domain (SErraND.org), and related assets. Users may not use, reproduce, or distribute SErraND’s intellectual property without prior written consent. User-generated content remains the property of the user, but by posting it, you grant SErraND a non-exclusive, royalty-free license to use, display, and distribute it on the platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Disclaimer of Employment</h2>
            <p>
              SErraND is a facilitator, not an employer of service providers. We connect clients with independent providers and do not control, supervise, or guarantee the quality, safety, or legality of their services. Clients are responsible for conducting due diligence before engaging a provider. SErraND is not liable for any issues arising from services provided.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Dispute Resolution Between Users</h2>
            <p>
              SErraND encourages users to resolve disputes directly. For remote services with verifiable proof, SErraND may offer mediation on a best-effort basis. The dispute resolution process is as follows:
            </p>
            <ol className="list-decimal list-inside mt-4 space-y-2 ml-4">
              <li><strong>Mediation</strong>: SErraND facilitates communication to reach a mutual resolution.</li>
              <li><strong>Arbitration</strong>: If mediation fails, disputes may proceed to arbitration under Kenyan law and the rules of a recognized arbitral institution.</li>
              <li><strong>Court Action</strong>: If arbitration is impractical, disputes may be escalated to Kenyan courts.</li>
            </ol>
            <p className="mt-4">
              Tort claims or allegations of damage must be addressed between the parties involved. Serious matters may be referred to Kenyan authorities.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Modification of Terms</h2>
            <p>
              SErraND may update these ToS at any time to reflect changes in our practices or legal requirements. We will notify users of material changes via website notices and/or email. Continued use of the platform after changes constitutes acceptance of the updated terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Force Majeure</h2>
            <p>
              SErraND is not liable for delays or failures in performance due to events beyond our control, such as natural disasters, war, terrorism, riots, strikes, or shortages of resources.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless SErraND, its officers, directors, employees, and agents from any claims, damages, or liabilities arising from your use of the platform, violation of these ToS, or infringement of any third-party rights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by Kenyan law, SErraND is not liable for indirect, incidental, special, or consequential damages, including loss of profits, data, or goodwill, arising from your use of the platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">12. Disclaimer of Warranty</h2>
            <p>
              SErraND provides the platform “as is” without warranties of any kind. We do not guarantee uninterrupted, secure, or error-free service, nor do we warrant the accuracy or reliability of results obtained from the platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">13. Consent to Electronic Communications</h2>
            <p>
              By using SErraND, you consent to receive electronic communications, including account notices, updates, and promotional messages via email or SMS. You may opt out of non-essential communications through your account settings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">14. Relationship of the Parties</h2>
            <p>
              No partnership, joint venture, or employment relationship is created between SErraND and users. Users have no authority to bind SErraND in any way.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">15. Entire Agreement</h2>
            <p>
              These ToS, along with the Privacy Policy, constitute the entire agreement between you and SErraND, superseding all prior agreements.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">16. Feedback Mechanism</h2>
            <p>
              We welcome your feedback to improve SErraND. Submit suggestions via our website’s feedback form or email <a href="mailto:serrand.support@serrand.org" className="text-blue-400 hover:underline">serrand.support@serrand.org</a>. By submitting feedback, you grant SErraND the right to use and adapt it without restriction.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">17. Governing Law and Jurisdiction</h2>
            <p>
              These ToS are governed by the laws of Kenya. Any disputes shall be resolved exclusively in the courts of Kenya.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">18. Contact Information</h2>
            <p>
              For questions about these ToS, contact us at <a href="mailto:serrand.support@serrand.org" className="text-blue-400 hover:underline">serrand.support@serrand.org</a>.
            </p>
          </section>
        </div>
      </section>

      {/* ───── FOOTER (Home.js style) ───── */}
      <footer className="bg-gray-900 bg-opacity-90 backdrop-blur-lg border-t border-gray-700 mt-16">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <Link to="/" className="flex items-center space-x-2 mb-4 group">
                <div className="logo-container bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent group-hover:from-blue-400 group-hover:to-purple-500 transition-all">
                  <span className="text-3xl font-bold">SE</span>
                  <span className="text-3xl font-bold">RRA</span>
                  <span className="text-3xl font-bold">ND</span>
                </div>
              </Link>
              <p className="text-gray-300 text-lg mb-4 leading-relaxed">
                Your trusted platform for connecting with local service providers across Kenya.
              </p>
              <AnimatedSocialLinks />
            </div>

            <div>
              <h3 className="text-white font-bold text-lg mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-300 hover:text-white transition-colors hover:underline">Home</Link></li>
                <li><Link to="/profile" className="text-gray-300 hover:text-white transition-colors hover:underline">Profile</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white transition-colors hover:underline">Contact</Link></li>
                <li><Link to="/search" className="text-gray-300 hover:text-white transition-colors hover:underline">Search Providers</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-bold text-lg mb-4">Legal & Support</h3>
              <ul className="space-y-2">
                <li><Link to="/terms" className="text-gray-300 hover:text-white transition-colors hover:underline">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-gray-300 hover:text-white transition-colors hover:underline">Privacy Policy</Link></li>
                <li><Link to="/about" className="text-gray-300 hover:text-white transition-colors hover:underline">About Us</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 mt-8 text-center">
            <p className="text-gray-400">© 2025 SErraND. All rights reserved. Made with love in Kenya</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
